import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryMarketingPage,
  industryMarketingHead,
  type IndustryFAQ,
} from "@/components/IndustryMarketingPage";

const FAQS: IndustryFAQ[] = [
  {
    q: "How do I market my electrical business in Colorado?",
    a: "Start with the basics that actually move the needle: a fully optimized Google Business Profile, local SEO targeting your service cities, and a website built to convert calls. Layer in Google Ads for emergency demand and GEO AI so you show up in AI-driven recommendations. Full guide: /answers/how-do-electricians-get-more-jobs-colorado.",
  },
  {
    q: "What SEO keywords should Colorado electricians rank for?",
    a: "A mix of emergency ('electrician near me', 'emergency electrician Colorado Springs'), service-specific ('panel upgrade', 'EV charger install', 'whole house rewire'), and commercial ('commercial electrician Denver') keywords across each city you serve. We map these out in our free audit.",
  },
  {
    q: "How do I get commercial electrical contracts through digital marketing?",
    a: "Commercial buyers research before they call. Win them with industry-specific landing pages, project case studies, LinkedIn presence, and SEO targeting commercial-intent keywords. PPC retargeting against past site visitors also closes a surprising amount of commercial bid work.",
  },
  {
    q: "Do Google Ads work well for electricians in Colorado Springs?",
    a: "Yes — when set up correctly. Emergency electrical service has very high intent and good margins, so a tightly targeted campaign with call tracking typically pays back fast. The wrong setup, however, can burn budget quickly — which is why we run ads in-house.",
  },
  {
    q: "How do I get more Google reviews for my electrical company?",
    a: "Build review-asks into your post-job process — text the customer a direct review link the same day work is completed. We help clients automate this so review velocity stays consistent, which is one of the biggest Google Maps ranking factors.",
  },
];

export const Route = createFileRoute("/electrical-contractor-marketing-colorado")({
  head: () =>
    industryMarketingHead(
      "Electrical Contractor",
      "electrical contractor",
      "electrical-contractor-marketing-colorado",
      FAQS,
    ),
  component: () => (
    <IndustryMarketingPage
      industry="Electrical Contractor"
      industryLower="electrical contractor"
      slug="electrical-contractor-marketing-colorado"
      subheadline="More service calls, more commercial bids, more revenue — for Colorado electricians."
      painPoints={[
        "Hard to differentiate online from unlicensed or underpriced competitors",
        "Commercial leads are high-value but nearly impossible to reach through traditional ads",
        "Google Business Profile not optimized, so nearby customers call someone else first",
      ]}
      serviceDescriptions={{
        seo: "Rank for emergency, residential, and commercial electrical searches across Colorado.",
        googleMaps: "Win the Map Pack for 'electrician near me' so nearby calls come to you.",
        webDesign: "Electrician websites that build trust and convert both residential and commercial leads.",
        ppc: "High-ROI Google Ads for emergency service and high-margin install jobs.",
        geoAi: "Get cited when Coloradans ask ChatGPT for a licensed local electrician.",
        socialMedia: "Showcase commercial projects and build the trust commercial buyers expect.",
      }}
      testimonial={{
        quote:
          "Booked solid through the busy season. We've had to hire two more techs to keep up with the leads.",
        role: "Electrical Contractor",
        city: "Pueblo",
      }}
      faqs={FAQS}
      industrySpokes={[
        {
          "to": "/electrical-seo-colorado",
          "label": "Electrical SEO in Colorado",
          "description": "Rank for panel upgrades, EV-charger installs, and emergency electrical work."
        },
        {
          "to": "/electrical-google-maps-colorado",
          "label": "Electrical Google Maps in Colorado",
          "description": "Map Pack ranking for Colorado electricians — GBP and review velocity."
        }
      ]}
      cityHubLinks={[
        {
          "to": "/colorado-springs",
          "label": "Electrical contractor marketing in Colorado Springs",
          "description": "Springs electrician SEO, Maps, and EV-install lead generation."
        },
        {
          "to": "/denver-digital-marketing",
          "label": "Electrical contractor marketing in Denver",
          "description": "Compete in Denver's electrical market with neighborhood-level local SEO."
        },
        {
          "to": "/boulder-digital-marketing",
          "label": "Electrical contractor marketing in Boulder",
          "description": "Boulder electrician SEO and EV-charger install lead generation."
        }
      ]}
    />
  ),
});
